import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { getWidgetTemplatesV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/get-widget-templates-v2-use-case";
import { WidgetTemplate } from "@src/types/dashboards";

interface UseWidgetTemplatesResult {
  templates: WidgetTemplate[];
  isLoading: boolean;
  error?: string;
}

/**
 * Loads the active widget-templates list (T1). Auth-conditional: the endpoint
 * requires the current `customerNamespace`, and on a hard refresh of a
 * dashboard route `authContext` populates asynchronously AFTER the panel
 * mounts. Without this gate the first fetch races the auth slice and
 * short-circuits with "Not authenticated"; the hook would then latch into an
 * empty-templates state forever because no later re-render can re-trigger it.
 * Depending on `authContext` makes the effect re-fire once the user session
 * resolves, so the static-tools library becomes clickable without a second
 * page load.
 */
export const useWidgetTemplates = (): UseWidgetTemplatesResult => {
  const [templates, setTemplates] = useState<WidgetTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  // Drives the effect: re-runs whenever the namespace-bearing auth context
  // flips (sign-in/out -> refetch for the new namespace).
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  useEffect(() => {
    // Auth not ready yet (cold reload of a protected route, or guarded
    // deep-link): wait. Auth populates = effect re-fires = fetch.
    if (!authContext) return;

    let cancelled = false;
    setIsLoading(true);
    setError(undefined);
    void (async () => {
      const result = await getWidgetTemplatesV2UseCase();
      if (cancelled) return;
      if (result.success && result.data) {
        setTemplates(result.data);
      } else {
        setError(result.error);
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [authContext]);

  return { templates, isLoading, error };
};
